import React from 'react'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '../../utils/chakraMonomorphic'

// Imported from the concrete module (not the coreApi/faces barrel) so a test
// that auto-mocks the barrel's transport calls doesn't also stub out this pure
// URL builder.
import {buildTrainingPhotoImageUrl} from '../../services/coreApi/faces/buildTrainingPhotoImageUrl'
import {RecognitionFace} from '../../types'
import {formatDate} from '../../utils/formatDate'
import {useRecognitionAttempts} from '../../hooks/useRecognitionAttempts'
import {buildRecognitionCsv, resolveCandidateName} from './recognitionCsv'

export interface FaceConfidenceModalProps {
  isOpen: boolean
  onClose: () => void
  photoId: number
  clientId: number
}

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`

// Render a (possibly malformed) RFC3339 training timestamp. A non-null but
// unparseable value would make `new Date(...)` an Invalid Date, which Intl
// throws a RangeError on; guard by validating the parse before formatting.
const formatTrainedLabel = (trainedUTC: string | null): string => {
  const parsed = trainedUTC ? new Date(trainedUTC) : null
  return parsed && !Number.isNaN(parsed.getTime())
    ? formatDate(parsed, 'longString')
    : 'unknown'
}

interface CandidateNameProps {
  personId: number | null
  name: string
  clientId: number
  trainedUTC: string | null
}

const CandidateName: React.FC<CandidateNameProps> = ({
  personId,
  name,
  clientId,
  trainedUTC,
}) => {
  const [showTrainingPhoto, setShowTrainingPhoto] = React.useState(false)

  // A null personId means there's no matched person to reveal a training photo
  // for, so render the name as plain (non-clickable) text.
  if (personId === null) {
    return <Text>{name}</Text>
  }

  return (
    <Box>
      <Button
        variant='link'
        colorScheme='blue'
        size='sm'
        onClick={(): void => setShowTrainingPhoto((prev) => !prev)}>
        {name}
      </Button>
      {showTrainingPhoto && (
        <Box mt={2}>
          <Image
            maxW='120px'
            alt={`Training photo for ${name}`}
            src={buildTrainingPhotoImageUrl(personId, clientId)}
          />
          <Text fontSize='xs' color='gray.500' mt={1} data-testid='trained-at'>
            Trained: {formatTrainedLabel(trainedUTC)}
          </Text>
        </Box>
      )}
    </Box>
  )
}

interface FaceSectionProps {
  face: RecognitionFace
  faceIndex: number
  clientId: number
  nameMap: Map<number, string>
}

const FaceSection: React.FC<FaceSectionProps> = ({
  face,
  faceIndex,
  clientId,
  nameMap,
}) => {
  // The acceptance threshold is constant across a scan's candidates, so show it
  // once as a note rather than repeating it in every row (frees the table
  // space and states "the current threshold level" once per Jeremy's review).
  const threshold = face.candidates[0]?.thresholdUsed

  return (
    <Box mb={6} data-testid='face-section'>
      <Text fontWeight='bold' mb={1}>
        Face {faceIndex + 1}
      </Text>
      <Text fontSize='sm' color='gray.500'>
        Attempted: {face.attemptedUTC}
      </Text>
      {threshold !== undefined && (
        <Text
          fontSize='sm'
          color='gray.500'
          mb={2}
          data-testid='threshold-note'>
          Acceptance threshold: {formatPercent(threshold)} — candidates at or
          above this are auto-accepted.
        </Text>
      )}
      <Table size='sm' variant='simple'>
        <Thead>
          <Tr>
            <Th>Rank</Th>
            <Th>Confidence</Th>
            <Th>Accepted</Th>
            <Th>Name</Th>
          </Tr>
        </Thead>
        <Tbody>
          {face.candidates.map((candidate) => (
            <Tr
              key={`${face.azureFaceId}-${candidate.rank}-${candidate.personId}`}>
              <Td>{candidate.rank}</Td>
              {/* null confidence = no candidate at this rank (SQL NULL);
                  render an em-dash rather than "0.0%". */}
              <Td>
                {candidate.confidence === null
                  ? '—'
                  : formatPercent(candidate.confidence)}
              </Td>
              <Td>{candidate.accepted ? 'Yes' : 'No'}</Td>
              <Td>
                <CandidateName
                  personId={candidate.personId}
                  name={resolveCandidateName(candidate.personId, nameMap)}
                  clientId={clientId}
                  trainedUTC={candidate.trainedUTC}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

export const FaceConfidenceModal: React.FC<FaceConfidenceModalProps> = ({
  isOpen,
  onClose,
  photoId,
  clientId,
}) => {
  const [showAllAttempts, setShowAllAttempts] = React.useState(false)

  const {status, data, nameMap, errorMessage} = useRecognitionAttempts({
    photoId,
    clientId,
    allScans: showAllAttempts,
    enabled: isOpen,
  })

  const faces = data?.faces ?? []
  const hasFaces = faces.length > 0
  const attemptCount = data?.attemptCount ?? 0

  const handleExportCsv = (): void => {
    if (!data) return
    const blob = new Blob([buildRecognitionCsv(data, nameMap)], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `fr-confidence-photo-${photoId}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Facial Recognition Confidence</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Only offer "show all attempts" when the photo actually has more
              than one scan batch — otherwise there is nothing more to show. */}
          {attemptCount > 1 && (
            <FormControl
              display='flex'
              alignItems='center'
              mb={4}
              data-testid='show-all-attempts-control'>
              <FormLabel htmlFor='show-all-attempts' mb={0}>
                Show all attempts
              </FormLabel>
              <Switch
                id='show-all-attempts'
                isChecked={showAllAttempts}
                onChange={(e): void => setShowAllAttempts(e.target.checked)}
              />
            </FormControl>
          )}

          {/* Always offer CSV export whenever there's data — CS may want to
              pull the numbers out of any photo, not just busy ones. */}
          {status === 'resolved' && hasFaces && (
            <Button
              size='sm'
              mb={4}
              onClick={handleExportCsv}
              data-testid='export-csv'>
              Export CSV
            </Button>
          )}

          {status === 'pending' && (
            <Flex justifyContent='center' py={8} data-testid='face-loading'>
              <Spinner />
            </Flex>
          )}

          {status === 'rejected' && (
            // Deliberately surfaces the real server message, including 4xx
            // detail. This is a staff-only diagnostic modal (gated by the
            // employee-only affordance + the server-side campminderstaff gate),
            // so exposing the underlying error is the point — it is the
            // diagnostic value, not a leak to end users.
            <Text color='red.500' data-testid='face-error'>
              {errorMessage ||
                'Something went wrong loading facial recognition data.'}
            </Text>
          )}

          {status === 'resolved' && !hasFaces && (
            <Text data-testid='face-empty'>
              No recognition data for this photo
            </Text>
          )}

          {status === 'resolved' &&
            hasFaces &&
            faces.map((face, faceIndex) => (
              <FaceSection
                key={face.azureFaceId}
                face={face}
                faceIndex={faceIndex}
                clientId={clientId}
                nameMap={nameMap}
              />
            ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
